import React, { useState, useMemo } from 'react';

// --- Internal Component: Acknowledgment Modal ---
const AcknowledgeModal = ({ onClose, onAcknowledge }) => {
    const [step, setStep] = useState(1);
    const [name, setName] = useState('');
    const [signature, setSignature] = useState('');
    const [isConfirmed, setIsConfirmed] = useState(false);
    const today = useMemo(() => new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }), []);

    const handleSignatureChange = (e) => {
        setSignature(e.target.value.toUpperCase().replace(/[^A-Z\s]/g, ''));
    };

    const handleFinalAcknowledge = () => {
        if (name && signature && isConfirmed) {
            onAcknowledge({ name, signature, date: today });
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-3xl w-full max-w-lg overflow-hidden transform transition-all scale-100">
                
                {/* Modal Header */}
                <div className="p-6 border-b border-gray-200 bg-indigo-600 flex justify-between items-center">
                    <h3 className="text-xl font-poppins font-bold text-white">
                        Protocol Acknowledgment (Step {step} of 3)
                    </h3>
                    <button onClick={onClose} className="text-white hover:text-indigo-100 transition">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 space-y-6">
                    {step === 1 && (
                        <div className="space-y-4">
                            <h4 className="font-poppins font-semibold text-gray-800">Compliance Statement Review</h4>
                            <p className="text-sm text-gray-600 leading-relaxed bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                I, the undersigned, confirm that I have read, understood, and agree to fully comply with all sections of the Field Work Safety Protocol, including all referenced Indian statutory requirements and emergency procedures.
                            </p>
                            <label className="flex items-center space-x-2 text-sm text-gray-700">
                                <input 
                                    type="checkbox" 
                                    checked={isConfirmed}
                                    onChange={(e) => setIsConfirmed(e.target.checked)}
                                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                />
                                <span>I confirm the above statement.</span>
                            </label>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4">
                            <h4 className="font-poppins font-semibold text-gray-800">Your Details</h4>
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name (as per HR records)</label>
                                <input
                                    type="text"
                                    id="name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-indigo-500 focus:border-indigo-500 font-roboto"
                                    placeholder="Enter your full legal name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Date of Acknowledgment</label>
                                <p className="mt-1 block w-full bg-gray-50 border border-gray-300 rounded-md shadow-sm p-3 text-gray-500 font-roboto">{today}</p>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-4">
                            <h4 className="font-poppins font-semibold text-gray-800">Electronic Signature (Type Your Initials)</h4>
                            <div>
                                <label htmlFor="signature" className="block text-sm font-medium text-gray-700">Initials (Max 3 Characters)</label>
                                <input
                                    type="text"
                                    id="signature"
                                    value={signature}
                                    maxLength={3}
                                    onChange={handleSignatureChange}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-lg p-4 text-xl tracking-widest text-center focus:ring-indigo-500 focus:border-indigo-500 font-poppins font-bold uppercase"
                                    placeholder="e.g., JDS"
                                />
                                <p className="mt-2 text-xs text-red-600">
                                    <span className="font-bold">Legal Notice:</span> This electronic signature is legally binding.
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="p-6 bg-gray-50 flex justify-end space-x-3">
                    {step > 1 && (
                        <button onClick={() => setStep(step - 1)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition">
                            Back
                        </button>
                    )}
                    {step < 3 && (
                        <button 
                            onClick={() => setStep(step + 1)} 
                            disabled={(step === 1 && !isConfirmed) || (step === 2 && !name)}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                                ((step === 1 && !isConfirmed) || (step === 2 && !name)) 
                                    ? 'bg-indigo-300 text-white cursor-not-allowed' 
                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                            }`}
                        >
                            Continue
                        </button>
                    )}
                    {step === 3 && (
                        <button 
                            onClick={handleFinalAcknowledge} 
                            disabled={signature.length === 0}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                                signature.length === 0
                                    ? 'bg-green-300 text-white cursor-not-allowed' 
                                    : 'bg-green-600 text-white hover:bg-green-700 shadow-md'
                            }`}
                        >
                            Acknowledge & Submit
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Main App Component ---
const App = () => {
    const [activeSection, setActiveSection] = useState('intro');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [acknowledgment, setAcknowledgment] = useState(null);

    // Data structure for the navigation and content mapping
    const sections = useMemo(() => [
        { id: 'intro', title: '1. Introduction & Scope', icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
        )},
        { id: 'planning', title: '2. Pre-Fieldwork Planning', icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2m-9 0v-2a2 2 0 012-2h2M9 5V3m2 4h4m-4 4h4m-4 4h4"></path></svg>
        )},
        { id: 'travel', title: '3. Travel & Transportation', icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path></svg>
        )},
        { id: 'health', title: '4. Health, Hygiene & PPE', icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path></svg>
        )},
        { id: 'comms', title: '5. Communication & Reporting', icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.669A9.865 9.865 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path></svg>
        )},
        { id: 'erp', title: '6. Emergency Response ', icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
        )},
    ], []);

    // Function to render the content for the active section
    const renderContent = () => {
        const baseClasses = "text-base leading-relaxed text-gray-700";
        const ulClasses = "list-disc ml-6 space-y-4 pt-2";
        const liClasses = "p-3 policy-list-item hover:shadow-sm transition-all duration-150";

        switch (activeSection) {
            case 'intro':
                return (
                    <div className="space-y-6">
                        <h2 className="text-3xl font-poppins font-bold text-indigo-800">Introduction & Scope</h2>
                        <p className={baseClasses}>
                            This comprehensive Protocol outlines mandatory safety procedures, standards, and legal compliance requirements for all personnel undertaking official work or travel outside the company premises. Compliance is paramount for protecting our employees, ensuring business continuity, meeting Indian statutory obligations, and upholding global health and safety best practices.
                        </p>
                        <div className="p-4 bg-indigo-50 border-l-4 border-indigo-500 rounded-lg">
                            <h3 className="font-poppins font-semibold text-indigo-700 mb-1">Applicability:</h3>
                            <p className="text-sm text-gray-700">This protocol is mandatory and applicable to all field activities, business travel, and temporary assignments within the entire territory of the Republic of India.</p>
                        </div>
                    </div>
                );
            case 'planning':
                return (
                    <div className="space-y-6">
                        <h2 className="text-3xl font-poppins font-bold text-indigo-700">Pre-Fieldwork Planning & Risk Assessment</h2>
                        <ul className={ulClasses}>
                            <li className={liClasses}><strong>Risk Identification & Mitigation:</strong> The Field Team Lead (or individual employee for solo travel) must complete a documented risk assessment, focusing on geographic hazards, infrastructure reliability, political stability, and specific activity risks.</li>
                            <li className={liClasses}><strong>Itinerary & Logistics Submission:</strong> A complete, approved travel and work itinerary, including backup communication plans, accommodation details, and local contacts, must be submitted to the Reporting Manager and Security Operations Centre (SOC) 72 hours prior to deployment.</li>
                            <li className={liClasses}><strong>Legal & Regulatory Compliance:</strong> Ensure all necessary local permits, clearances, and official permissions (e.g., from local police, village elders, or specific industry regulators) are obtained and carried as per Indian law.</li>
                            <li className={liClasses}><strong>Insurance & Medical Clearance:</strong> Confirm that the corporate travel, personal accident, and medical insurance policies are active and adequate for the fieldwork location, including coverage for evacuation if necessary.</li>
                        </ul>
                    </div>
                );
            case 'travel':
                return (
                    <div className="space-y-6">
                        <h2 className="text-3xl font-poppins font-bold text-indigo-700">Travel & Transportation Safety</h2>
                        <ul className={ulClasses}>
                            <li className={liClasses}><strong>Approved Transportation:</strong> Use only pre-approved, commercial, or company-verified transport providers. Employees must verify vehicle registration and driver credentials, ensuring compliance with the <span className="font-bold text-indigo-600">Motor Vehicles Act, 1988 (India)</span>.</li>
                            <li className={liClasses}><strong>Restricted Movement:</strong> Avoid non-essential travel between sunset and sunrise. If unavoidable, secure approval, travel in teams of two or more, and maintain location tracking.</li>
                            <li className={liClasses}><strong>Data and Asset Security:</strong> Laptops and company data must be encrypted. Physical assets should remain secured, never left unattended, and transported only in locked compartments.</li>
                            <li className={liClasses}><strong>Seatbelt Mandate:</strong> All personnel must wear seatbelts at all times when the vehicle is in motion, in compliance with traffic regulations.</li>
                        </ul>
                    </div>
                );
            case 'health':
                return (
                    <div className="space-y-6">
                        <h2 className="text-3xl font-poppins font-bold text-indigo-700">Health, Hygiene, and Equipment</h2>
                        <ul className={ulClasses}>
                            <li className={liClasses}><strong>Personal Protective Equipment (PPE):</strong> Appropriate PPE (e.g., safety footwear, hard hats, high-visibility vests) must be used as mandated by local work environment regulations or specific project requirements.</li>
                            <li className={liClasses}><strong>First-Aid & Emergency Kits:</strong> Each field team must carry a fully stocked, readily accessible Level 2 First Aid Kit, clearly marked. All team members must know how to access and use the contents.</li>
                            <li className={liClasses}><strong>Food and Water Safety:</strong> Only consume sealed, bottled water or water purified through reliable company-provided filtration systems. Exercise extreme caution with local street food and ensure all meals are prepared hygienically.</li>
                            <li className={liClasses}><strong>Wellness Check:</strong> Any employee feeling unwell must immediately notify their manager and seek appropriate medical attention. Do not attempt to mask symptoms or continue working if incapacitated.</li>
                        </ul>
                    </div>
                );
            case 'comms':
                return (
                    <div className="space-y-6">
                        <h2 className="text-3xl font-poppins font-bold text-indigo-700">Communication and Reporting</h2>
                        <ul className={ulClasses}>
                            <li className={liClasses}><strong>Scheduled Check-ins:</strong> Daily mandatory check-ins (morning start, midday status, evening close) must be maintained via pre-defined channels (e.g., corporate chat, satellite phone, or secure SMS).</li>
                            <li className={liClasses}><strong>Loss of Contact Protocol:</strong> If a check-in is missed, the manager must attempt contact immediately. After 60 minutes of no contact, the manager initiates the SOC Missing Person/Comms Failure protocol.</li>
                            <li className={liClasses}><strong>Incident Reporting:</strong> Any accident, injury, near-miss, property damage, security threat, or violation of local laws must be documented and reported to the SOC and HR within 4 hours of the incident.</li>
                            <li className={liClasses}><strong>Whistleblower Protection:</strong> Employees are encouraged to report any breaches of this protocol without fear of reprisal, as protected by company policy.</li>
                        </ul>
                    </div>
                );
            case 'erp':
                return (
                    <div className="p-5 bg-red-50 border border-red-200 rounded-xl space-y-4">
                        <h2 className="text-3xl font-poppins font-bold text-red-800 flex items-center">
                            <svg className="w-8 h-8 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                            Emergency Response Protocol (ERP)
                        </h2>
                        <p className="text-lg leading-relaxed font-bold text-red-700">In case of any emergency, the following immediate actions must be taken:</p>
                        <ul className="list-disc ml-6 space-y-3 text-base leading-relaxed mt-4">
                            <li><strong>Immediate Safety First:</strong> Assess the situation. Move yourself and colleagues to a secure location if it is safe to do so. Do not engage threats.</li>
                            <li><strong>Pan-India Emergency Services:</strong> Utilize the universal emergency number for immediate help:
                                <ul className="list-none ml-4 mt-2 space-y-1 font-roboto">
                                    <li><span className="font-extrabold text-white bg-red-700 px-3 py-1 rounded mr-2 inline-block min-w-[50px] text-center">112:</span> All-in-one Emergency Response Support System (ERSS - Police, Fire, Ambulance).</li>
                                    <li><span className="font-extrabold text-white bg-red-700 px-3 py-1 rounded mr-2 inline-block min-w-[50px] text-center">100:</span> Police (Direct).</li>
                                    <li><span className="font-extrabold text-white bg-red-700 px-3 py-1 rounded mr-2 inline-block min-w-[50px] text-center">108:</span> Ambulance/Emergency Services (Health).</li>
                                </ul>
                            </li>
                            <li><strong>Internal Contact:</strong> After contacting local emergency services, immediately call the Company Security Operations Centre (SOC) at the dedicated 24/7 hotline (Internal Contact Placeholder).</li>
                        </ul>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <style>
                {/* CSS and Font Definitions (kept outside JSX for cleaner syntax) */}
                {`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800&family=Roboto:wght@400;500;700&display=swap');
                .font-poppins { font-family: 'Poppins', sans-serif; }
                .font-roboto { font-family: 'Roboto', sans-serif; }
                .policy-content-container {
                    scrollbar-width: thin;
                    scrollbar-color: #3b82f6 #e5e7eb;
                }
                .policy-content-container::-webkit-scrollbar {
                    width: 8px;
                }
                .policy-content-container::-webkit-scrollbar-thumb {
                    background-color: #3b82f6; /* Blue-500 for scrollbar */
                    border-radius: 4px;
                }
                .policy-content-container::-webkit-scrollbar-track {
                    background-color: #e5e7eb;
                }
                .nav-link:hover {
                    background-color: #f3f4f6;
                    transition: background-color 0.2s;
                }
                `}
            </style>
            
            <div className="bg-gray-100 min-h-screen p-4 sm:p-8 font-roboto text-gray-700">
                <div className="max-w-7xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden min-h-[85vh]">
                    
                    {/* Header Section */}
                    <header className="p-6 sm:p-8 border-b border-gray-200 bg-white sticky top-0 z-20 flex justify-between items-center">
                        <div className="flex items-center">
                            <svg className="w-10 h-10 mr-4 text-indigo-700 p-1 bg-indigo-50 rounded-full flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.275a1.988 1.988 0 010 2.804l-7.142 7.143a1.988 1.988 0 01-2.804 0l-4.275-4.275a1.988 1.988 0 010-2.804l7.142-7.143a1.988 1.988 0 012.804 0l4.275 4.275z"></path></svg>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-poppins font-extrabold text-gray-900">
                                    Field Work Safety Protocol
                                </h1>
                                <p className="text-sm text-gray-500 font-roboto">
                                    Mandatory HRMS Compliance Document (India)
                                </p>
                            </div>
                        </div>

                        {/* Acknowledge Button */}
                        {acknowledgment ? (
                            <div className="text-sm font-medium text-green-700 bg-green-100 p-2.5 rounded-lg border border-green-200">
                                Acknowledged by: {acknowledgment.signature} on {acknowledgment.date}
                            </div>
                        ) : (
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="px-6 py-3 bg-indigo-600 text-white font-poppins font-semibold text-sm rounded-xl shadow-lg hover:bg-indigo-700 transition transform hover:scale-[1.02] active:scale-100 focus:outline-none focus:ring-4 focus:ring-indigo-500 focus:ring-opacity-50"
                            >
                                Acknowledge Protocol
                            </button>
                        )}
                    </header>

                    {/* Main Content Layout (Sidebar + Content) */}
                    <div className="flex flex-col lg:flex-row">
                        
                        {/* Sidebar Navigation */}
                        <nav className="lg:w-72 flex-shrink-0 border-b lg:border-r border-gray-200 p-4 bg-gray-50 lg:h-auto overflow-x-auto">
                            <div className="lg:sticky lg:top-24 space-y-2">
                                {sections.map(section => (
                                    <button
                                        key={section.id}
                                        onClick={() => setActiveSection(section.id)}
                                        className={`nav-link w-full text-left flex items-center p-3 rounded-lg transition-all ${
                                            activeSection === section.id 
                                                ? 'bg-indigo-100 text-indigo-700 font-semibold border-l-4 border-indigo-600'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        <span className="mr-3">{section.icon}</span>
                                        <span className="font-roboto text-sm flex-grow whitespace-nowrap">{section.title}</span>
                                    </button>
                                ))}
                            </div>
                        </nav>
                        
                        {/* Policy Content */}
                        <main className="flex-grow p-6 sm:p-8 overflow-y-auto policy-content-container">
                            <div className="space-y-10">
                                {renderContent()}
                            </div>
                        </main>
                    </div>
                </div>

                {/* Conditional Modal Render */}
                {isModalOpen && <AcknowledgeModal 
                    onClose={() => setIsModalOpen(false)} 
                    onAcknowledge={setAcknowledgment} 
                />}
            </div>
        </>
    );
};

export default App;